import React, { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getSuperAdminCompany } from "../../service/api";
import {
  Card, Kpi, Delta, Pill, PeriodPicker, PRESETS, StatusBars, Funnel, useSort,
  fmtNum, fmtMoney, fmtMoneyObj, fmtPct, fmtDate, fmtHours, healthPill, rateCardLabel, rateCategoryLabel, isSuperAdmin, Forbidden, Loading
} from "./ui";
import { TrendLine, MoneyTrend } from "./charts";

export default function CompanyIntelligence() {
  const { customerId } = useParams();
  const navigate = useNavigate();
  const [preset, setPreset] = useState("30d");
  const [range, setRange] = useState(PRESETS[0].range());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(() => {
    setLoading(true);
    getSuperAdminCompany(customerId, range)
      .then((res) => setData(res?.data || null))
      .catch((err) => { console.error(err); toast.error(err?.response?.data?.message || "Could not load the company"); })
      .finally(() => setLoading(false));
  }, [customerId, range]);

  useEffect(() => { load(); }, [load]);

  if (!isSuperAdmin()) return <Forbidden />;
  if (loading && !data) return <div className="intel-page"><Loading /></div>;
  if (!data) return <div className="intel-page"><div className="intel-card"><div className="intel-empty">Company not found.</div></div></div>;

  const c = data.company;
  const k = data.kpis;
  const currency = data.priceCard?.[0]?.currency || (k.revenue?.USD > k.revenue?.INR ? "USD" : "INR");
  const initials = (c.companyName || "?").split(" ").map((w) => w[0]).join("").slice(0, 2).toUpperCase();

  return (
    <div className="intel-page">
      <div className="intel-crumbs"><Link to="/admin/intelligence">Business Intelligence</Link><span>›</span><span>Companies</span><span>›</span><span className="strong" style={{ color: "#3A3A3A" }}>{c.companyName}</span></div>

      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 16, flexWrap: "wrap" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <div style={{ width: 52, height: 52, borderRadius: 6, background: "#3E67B1", color: "#FFF", display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: 20 }}>{initials}</div>
          <div>
            <h1 className="intel-title">{c.companyName} {healthPill(c.health)} {!c.priced && <Pill tone="amber">No rate card set</Pill>}</h1>
            <p className="intel-subtitle">Customer since {fmtDate(c.customerSince)}{c.country ? ` · ${c.country}` : ""} · {fmtNum(c.openJobs)} open jobs · ticket #{c.ticketId}</p>
          </div>
        </div>
        <div style={{ display: "flex", gap: 10 }}>
          <button className="intel-btn secondary" type="button" onClick={() => navigate(`/admin/updateTicket/${c.ticketId}`)}>Edit rate card (ticket)</button>
          <button className="intel-btn secondary" type="button" onClick={() => navigate("/admin/interviewSchedules/all")}>Interview schedules</button>
        </div>
      </div>

      <div className="intel-toolbar">
        <PeriodPicker preset={preset} setPreset={setPreset} range={range} setRange={setRange} />
        <div className="spacer" />
        <span className="intel-updated">{fmtDate(data.period.startDate)} – {fmtDate(data.period.endDate)} · compared with the {data.period.days} days before</span>
      </div>

      <div className="intel-grid cols-5">
        <Kpi label="Interviews" value={fmtNum(k.interviews)} sub={<Delta value={k.changePct} />} />
        <Kpi label="Completed · feedback" value={`${fmtNum(k.completed)} · ${fmtNum(k.feedbackProvided)}`} sub={k.pendingFeedback ? `${k.pendingFeedback} waiting over 48 h` : "No feedback overdue"} tone={k.pendingFeedback ? "warn" : ""} help="Completed interviews and how many already have panel feedback." />
        <Kpi label="Revenue billed" value={fmtMoneyObj(k.revenue)} sub={`Panel cost ${fmtMoneyObj(k.panelCost)}`} help="From this customer's rate card by interview status." />
        <Kpi label="Gross margin" value={fmtMoneyObj(k.margin)} valueTone={(k.margin?.[currency] || 0) < 0 ? "bad" : "good"} sub={<span>{fmtPct(k.marginPct)} · <Delta value={Math.round((k.marginPct - k.priorMarginPct) * 10) / 10} pts suffix="" /></span>} />
        <Kpi label="Quality" value={fmtPct(k.cancelLikeRate)} valueTone={k.cancelLikeRate >= 10 ? "bad" : ""} sub={`Cancel + no-show · feedback in ${fmtHours(k.medianFeedbackHours)} median · ${fmtPct(k.selectedRate, 0)} selected`} help="Share of interviews cancelled or no-show. Feedback time is from the interview day to the panel's feedback." />
      </div>

      {data.notes?.length > 0 && (
        <Card title="What stands out" hint="Rule-based observations for this period. No AI is used.">
          <div className="intel-card-body">
            <ul style={{ margin: 0, paddingLeft: 18, fontSize: 13, lineHeight: 1.8 }}>{data.notes.map((n, i) => <li key={i}>{n}</li>)}</ul>
          </div>
        </Card>
      )}

      <div className="intel-grid cols-2">
        <Card title="Price card and margin by rate type" hint="Price = billed per completed interview · from the customer's ticket" superOnly>
          <table className="intel-table">
            <thead><tr><th>Rate type</th><th className="num">Price</th><th className="num">Avg panel cost</th><th className="num">Margin / interview</th><th className="num">Interviews</th></tr></thead>
            <tbody>
              {data.priceCard.map((r) => (
                <tr key={`${r.rateCategory}${r.rateCard}`} className={r.priceCompleted && r.marginPerInterview < 0 ? "hl-red" : ""}>
                  <td className="strong">{rateCardLabel(r.rateCard)} <span className="muted">· {rateCategoryLabel(r.rateCategory)}</span></td>
                  <td className="num">{r.priceCompleted ? fmtMoney(r.priceCompleted, r.currency, false) : <span className="warn">not set</span>}</td>
                  <td className="num">{fmtMoney(r.avgPanelCost, r.currency, false)}</td>
                  <td className={`num strong ${r.marginPerInterview < 0 ? "bad" : "good"}`}>{r.priceCompleted ? `${fmtMoney(r.marginPerInterview, r.currency, false)} · ${fmtPct(r.marginPct, 0)}` : "–"}</td>
                  <td className="num">{fmtNum(r.interviews)}</td>
                </tr>
              ))}
              {!data.priceCard.length && <tr><td colSpan={5} className="intel-empty">No interviews in this period.</td></tr>}
            </tbody>
          </table>
          {data.priceCard.length > 0 && (
            <div className="intel-card-foot">
              <span>Also billed: cancellation {data.priceCard[0].priceCancelled ? fmtMoney(data.priceCard[0].priceCancelled, currency, false) : "–"} · candidate no-show {data.priceCard[0].priceCandidateNoShow ? fmtMoney(data.priceCard[0].priceCandidateNoShow, currency, false) : "–"} · reschedule {data.priceCard[0].priceRescheduled ? fmtMoney(data.priceCard[0].priceRescheduled, currency, false) : "–"} ({rateCardLabel(data.priceCard[0].rateCard)})</span>
            </div>
          )}
        </Card>
        <Card title="Interview status" hint={`${fmtNum(data.statusMix.total)} interviews`}>
          <div className="intel-card-body"><StatusBars mix={data.statusMix} /></div>
        </Card>
      </div>

      <PanelsTable panels={data.panels} currency={currency} />

      <div className="intel-grid cols-2">
        <Card title="Interview volume, last 6 months"><div className="intel-card-body"><TrendLine trend={data.trend} /></div></Card>
        <Card title="Revenue and panel cost, last 6 months" superOnly><div className="intel-card-body"><MoneyTrend trend={data.trend} currency={currency} /></div></Card>
      </div>

      <div className="intel-grid two-one">
        <JobsTable jobs={data.jobs} />
        <Card title="Hiring funnel" hint="Candidates created in the period"><div className="intel-card-body"><Funnel funnel={data.funnel} /></div></Card>
      </div>
    </div>
  );
}

function PanelsTable({ panels, currency }) {
  const { sorted, Th } = useSort(panels, "interviews");
  return (
    <Card title="Panels serving this company and what each is paid" hint="Paid = average panel cost for a completed interview · Price = what this company is billed" superOnly>
      <div className="intel-scroll">
        <table className="intel-table">
          <thead>
            <tr>
              <Th k="panelName">Panel</Th><Th k="skillFamily">Skill family</Th><th>Rate card</th>
              <Th k="paidPerInterview" num>Paid / interview</Th><Th k="companyPrice" num>Company price</Th><Th k="marginPerInterview" num>Margin</Th>
              <Th k="interviews" num>Interviews</Th><Th k="noShows" num>No-shows</Th><Th k="reschedules" num>Reschedules</Th><Th k="medianFeedbackHours" num>Feedback time</Th><Th k="totalPaid" num>Total paid</Th>
            </tr>
          </thead>
          <tbody>
            {sorted.map((x) => (
              <tr key={x.panelEmailId} className={x.companyPrice && x.marginPerInterview < 0 ? "hl-red" : x.noShows >= 2 ? "hl-amber" : ""}>
                <td><div className="strong">{x.panelName}</div><div className="muted" style={{ fontSize: 11 }}>{x.panelEmailId}</div></td>
                <td>{x.skillFamily || "–"}</td>
                <td>{rateCategoryLabel(x.rateCategory)} · {rateCardLabel(x.rateCard)}</td>
                <td className="num">{fmtMoney(x.paidPerInterview, x.currency, false)}</td>
                <td className="num">{x.companyPrice ? fmtMoney(x.companyPrice, x.currency, false) : <span className="warn">not set</span>}</td>
                <td className={`num strong ${x.companyPrice ? (x.marginPerInterview < 0 ? "bad" : "good") : "muted"}`}>{x.companyPrice ? fmtMoney(x.marginPerInterview, x.currency, false) : "–"}</td>
                <td className="num">{fmtNum(x.interviews)}</td>
                <td className={`num ${x.noShows >= 2 ? "warn strong" : ""}`}>{fmtNum(x.noShows)}</td>
                <td className="num">{fmtNum(x.reschedules)}</td>
                <td className="num">{fmtHours(x.medianFeedbackHours)}</td>
                <td className="num">{fmtMoney(x.totalPaid, x.currency, false)}</td>
              </tr>
            ))}
            {!sorted.length && <tr><td colSpan={11} className="intel-empty">No panels in this period.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="intel-card-foot"><span>Red rows: the panel is paid more than the company is billed. Amber rows: two or more panel no-shows.</span></div>
    </Card>
  );
}

function JobsTable({ jobs }) {
  const open = (jobs || []).filter((j) => (j.status || "").toUpperCase() !== "CLOSED");
  const closed = (jobs || []).length - open.length;
  return (
    <Card title="Open jobs" hint={`${open.length} open · ${open.filter((j) => j.stalled).length} stalled · ${closed} closed`}>
      <div className="intel-scroll" style={{ maxHeight: 420 }}>
        <table className="intel-table">
          <thead><tr><th>Job</th><th>Skill family</th><th className="num">Positions</th><th className="num">Candidates</th><th className="num">Scheduled</th><th className="num">Completed</th><th className="num">Selected</th><th>Last activity</th><th>Status</th></tr></thead>
          <tbody>
            {open.map((j) => (
              <tr key={j.jobId} className={j.stalled ? "hl-amber" : ""}>
                <td className="strong">{j.jobName}</td><td>{j.skillFamily || "–"}</td><td className="num">{fmtNum(j.positionsRequired)}</td>
                <td className="num">{fmtNum(j.candidates)}</td><td className="num">{fmtNum(j.scheduled)}</td><td className="num">{fmtNum(j.completed)}</td><td className="num">{fmtNum(j.selected)}</td>
                <td>{j.lastActivity ? `${fmtDate(j.lastActivity)}${j.daysSinceActivity === 0 ? " (today)" : ` (${j.daysSinceActivity} d ago)`}` : "–"}</td>
                <td>{j.stalled ? <Pill tone="amber">Stalled</Pill> : <Pill tone="green">{j.status || "Active"}</Pill>}</td>
              </tr>
            ))}
            {!open.length && <tr><td colSpan={9} className="intel-empty">No open jobs.</td></tr>}
          </tbody>
        </table>
      </div>
      <div className="intel-card-foot"><span>Stalled = open job with no candidate, schedule or job update for 10 or more days.</span></div>
    </Card>
  );
}
